import { Box, createStyles, keyframes } from '@mantine/core';
import { Banner } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';

const slideUp = keyframes({
	from: {
		transform: 'translateY(100%)',
	},
	to: {
		transform: 'translateY(0)',
	},
});

const useStyles = createStyles(() => ({
	bannerContainer: {
		animation: `${slideUp} 0.3s ease-out`,
	},
}));

enum BannerType {
	EDUCATION = 'education',
	MESSAGE_LIMIT = 'message_limit',
	CREDIT_RESTRICTION = 'credit_restriction',
}

const ChatBanner = observer(
	({
		showQuestionEducationBanner,
		showMessageLimitBanner,
		showCreditRestrictionBanner,
		creditRestrictionReason,
		onDismissQuestionEducationBanner,
		onDismissMessageLimitBanner,
	}: {
		showQuestionEducationBanner: boolean;
		showMessageLimitBanner: boolean;
		showCreditRestrictionBanner: boolean;
		creditRestrictionReason?: string;
		onDismissQuestionEducationBanner: () => void;
		onDismissMessageLimitBanner: () => void;
	}) => {
		const { classes } = useStyles();

		const visibleBanner = useMemo(() => {
			if (showCreditRestrictionBanner) return BannerType.CREDIT_RESTRICTION;
			if (showQuestionEducationBanner) return BannerType.EDUCATION;
			if (showMessageLimitBanner) return BannerType.MESSAGE_LIMIT;
			return null;
		}, [
			showQuestionEducationBanner,
			showMessageLimitBanner,
			showCreditRestrictionBanner,
		]);

		const handleBannerDismiss = useCallback(
			(bannerType: BannerType) => {
				switch (bannerType) {
					case BannerType.EDUCATION:
						onDismissQuestionEducationBanner();
						break;
					case BannerType.MESSAGE_LIMIT:
						onDismissMessageLimitBanner();
						break;
					case BannerType.CREDIT_RESTRICTION:
						// Credit restriction banner cannot be dismissed
						break;
					default:
						break;
				}
			},
			[onDismissQuestionEducationBanner, onDismissMessageLimitBanner]
		);

		const content = useMemo(() => {
			switch (visibleBanner) {
				case BannerType.EDUCATION:
					return {
						title: "Not getting the answer you're looking for?",
						message:
							"If Secoda AI isn't providing the answer you need, you can forward your question to your team. Just select the question icon on a message to get started.",
					};
				case BannerType.MESSAGE_LIMIT:
					return {
						message:
							'After 10 messages inaccuracies may occur. Please consider starting a new chat to maintain high response quality.',
					};
				case BannerType.CREDIT_RESTRICTION:
					return {
						title: 'AI Access Restricted',
						message:
							creditRestrictionReason ||
							'Insufficient credit balance – add credits to continue using AI features',
						tone: 'warning' as const,
					};
				default:
					return null;
			}
		}, [visibleBanner, creditRestrictionReason]);

		if (!visibleBanner || !content) {
			return null;
		}

		return (
			<Box pb="xs" style={{ overflow: 'hidden' }}>
				<Box className={classes.bannerContainer}>
					<Banner
						tone={content.tone || 'neutral'}
						{...content}
						onDismiss={() => handleBannerDismiss(visibleBanner)}
					/>
				</Box>
			</Box>
		);
	}
);

export { ChatBanner };
